import i18n from "i18next";
import { emailSignatureBASE64 } from "../../../assets/base64/email-signature";
import { Etablissement } from "../../../schemas-and-types/etablissement/etablissemen.schema";

const websiteUrl = process.env.REACT_APP_WEBSITE_URL;

/**
 * Generate a registration confirmation email with translations
 */
export function generateInscriptionEmail(
  prenom: string,
  nom: string,
  etablissementInfo: Etablissement
) {
  return `<article>
      <p>
        ${i18n.t("email.greeting")},
      </p>
      <p>
        ${i18n.t("email.registration.thankYou", { establishmentName: etablissementInfo.name })}
      </p>
      <p>${i18n.t("email.registration.summary")}</p>
      <p>
        ${i18n.t("email.registration.correction", { deadline: process.env.REACT_APP_DATE_END_INSCRIPTION_LITERAL })}
      </p>
      <p>${i18n.t("email.regards")}</p>
      <p>${i18n.t("email.team")}</p>
      <br></br>
      <br></br>
      <h3>${i18n.t("email.registration.summary")}:</h3>
      <br></br>
      <strong>${i18n.t("email.registration.establishmentInfo")}</strong>
      <p>${i18n.t("email.registration.establishmentType")}: ${etablissementInfo.type}</p>
      <p>${i18n.t("email.registration.establishmentName")}: ${etablissementInfo.name}</p>
      <p>
        ${i18n.t("email.registration.establishmentAddress")}: ${etablissementInfo.localisation.address}
      </p>
      <p>
        ${i18n.t("email.registration.accessAndTransport")}: ${etablissementInfo.information_access}
      </p>
      <p>
        ${i18n.t("email.registration.operationalRegion")}: ${etablissementInfo.information_regional.region}
      </p>
      <p>
        ${i18n.t("email.registration.drName")}: ${etablissementInfo.information_regional.nom_dr}
      </p>
      <p>
        ${i18n.t("email.registration.drFirstName")}: ${etablissementInfo.information_regional.prenom_dr}
      </p>
      <br></br>
      <strong>${i18n.t("email.registration.referentInfo")}</strong>
      <p>
        ${i18n.t("email.registration.referentName")}: ${etablissementInfo.information_referent?.nom_referent || i18n.t("email.registration.notProvided")}
      </p>
      <p>
        ${i18n.t("email.registration.referentFirstName")}: ${etablissementInfo.information_referent?.prenom_referent || i18n.t("email.registration.notProvided")}
      </p>
      <p>
        ${i18n.t("email.registration.email")}: ${etablissementInfo.information_referent?.email_referent || i18n.t("email.registration.notProvided")}
      </p>
      <p>
        ${i18n.t("email.registration.phone")}: ${etablissementInfo.information_referent?.phone_referent || i18n.t("email.registration.notProvided")}
      </p>
      <br></br>
      <p>
        ${i18n.t("email.registration.viewOptions")}:
        ${websiteUrl}
      </p>
      <br></br>
      <p>${i18n.t("email.regards")}</p>
      <p>${i18n.t("email.organizingTeam")}</p>
      <br></br>
    </article>`;
}

/**
 * Generate a registration modification email with translations
 */
export function generateInscriptionModificationEmail(
  prenom: string,
  nom: string,
  etablissementInfo: Etablissement
) {
  return `<article>
      <p>
        ${i18n.t("email.greeting")},
      </p>

      <p>
        ${i18n.t("email.modification.changes")}
      </p>

      <p>${i18n.t("email.modification.summary")}</p>

      <p>
        ${i18n.t("email.modification.modification", { deadline: process.env.REACT_APP_DATE_END_INSCRIPTION_LITERAL })}
      </p>

      <p>${i18n.t("email.regards")}</p>

      <p>${i18n.t("email.team")}</p>

      <b>${i18n.t("email.modification.modificationSummary")}:</b>
      <br></br>
      <strong>${i18n.t("email.registration.establishmentInfo")}</strong>
      <p>${i18n.t("email.registration.establishmentType")}: ${etablissementInfo.type}</p>
      <p>${i18n.t("email.registration.establishmentName")}: ${etablissementInfo.name}</p>
      <p>
        ${i18n.t("email.registration.establishmentAddress")}: ${etablissementInfo.localisation.address}
      </p>
      <p>
        ${i18n.t("email.registration.accessAndTransport")}: ${etablissementInfo.information_access}
      </p>
      <p>
        ${i18n.t("email.registration.operationalRegion")}: ${etablissementInfo.information_regional.region}
      </p>
      <p>
        ${i18n.t("email.registration.drName")}: ${etablissementInfo.information_regional.nom_dr}
      </p>
      <p>
        ${i18n.t("email.registration.drFirstName")}: ${etablissementInfo.information_regional.prenom_dr}
      </p>
      <br></br>
      <strong>${i18n.t("email.registration.referentInfo")}</strong>
      <p>
        ${i18n.t("email.registration.referentName")}: ${etablissementInfo.information_referent?.nom_referent || i18n.t("email.registration.notProvided")}
      </p>
      <p>
        ${i18n.t("email.registration.referentFirstName")}: ${etablissementInfo.information_referent?.prenom_referent || i18n.t("email.registration.notProvided")}
      </p>
      <p>
        ${i18n.t("email.registration.email")}: ${etablissementInfo.information_referent?.email_referent || i18n.t("email.registration.notProvided")}
      </p>
      <p>
        ${i18n.t("email.registration.phone")}: ${etablissementInfo.information_referent?.phone_referent || i18n.t("email.registration.notProvided")}
      </p>
      <br></br>
      <p>
        ${i18n.t("email.registration.viewOptions")}:
        ${websiteUrl}
      </p>
      <br></br>
    </article>`;
}

/**
 * Generate a registration cancellation email with translations
 */
export function generateCancelInscriptionEmail(prenom: string, nom: string) {
  return `
    <article>
      <p>
        ${i18n.t("email.greeting")},
      </p>
      <p>
        ${i18n.t("email.cancellation.cancelled")}
      </p>
      <p>
        ${i18n.t("email.cancellation.reconsider", { deadline: process.env.REACT_APP_DATE_END_INSCRIPTION_LITERAL })}
      </p>
      <br></br>
      <p>${i18n.t("email.regards")}</p>
      <p>${i18n.t("email.team")}</p>
      <br></br>
      <img
      src="${emailSignatureBASE64}"
      alt="signature_logo"
      width={100}
      height={100}
      ></img>
    </article>`;
}
